import React, { useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, CircularProgress } from '@mui/material';

// Layout-Komponenten
import Layout from './components/layout/Layout';

// Seiten
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import Projects from './pages/Projects';
import TimeEntries from './pages/TimeEntries';
import Users from './pages/Users';
import Reports from './pages/Reports';
import NotFound from './pages/NotFound';
import ChangePassword from './pages/ChangePassword';

// Kontext
import { AuthContext, AuthProvider } from './context/AuthContext';
import { FeedbackProvider } from './context/FeedbackContext';

// Farben und Typografie für das Material UI-Thema
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2'
    },
    secondary: {
      main: '#dc004e'
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff'
    }
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 500
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 500
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 500
    }
  }
});

const Ladeanzeige = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh'
    }}
  >
    <CircularProgress />
  </Box>
);

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
}

const ProtectedRoute = ({ children, allowedRoles = [] }: ProtectedRouteProps) => {
  const { isAuthenticated, user, initialLoading } = useContext(AuthContext);
  const location = useLocation();

  if (initialLoading) {
    return <Ladeanzeige />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (allowedRoles.length === 0) {
    return <>{children}</>;
  }

  if (user && allowedRoles.includes(user.role)) {
    return <>{children}</>;
  }

  // Rolle passt nicht: zurück zum Dashboard. Der eigentliche Schutz liegt
  // im Backend – diese Prüfung blendet nur unerreichbare Seiten aus.
  return <Navigate to="/" replace />;
};

const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, initialLoading } = useContext(AuthContext);

  if (initialLoading) {
    return <Ladeanzeige />;
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <FeedbackProvider>
        <AuthProvider>
          <Router>
            <Routes>
              {/*
                Öffentlich ist nur noch der Login. Die Route /register wurde
                entfernt – Konten legt ausschließlich ein Admin an.
              */}
              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <Login />
                  </PublicRoute>
                }
              />
              <Route path="/register" element={<Navigate to="/login" replace />} />

              {/* Geschützte Routen innerhalb des Layouts */}
              <Route path="/" element={<Layout />}>
                <Route
                  index
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="projects"
                  element={
                    <ProtectedRoute allowedRoles={['Admin', 'Projektleiter']}>
                      <Projects />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="time-entries"
                  element={
                    <ProtectedRoute>
                      <TimeEntries />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="reports"
                  element={
                    <ProtectedRoute allowedRoles={['Admin', 'Projektleiter']}>
                      <Reports />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="users"
                  element={
                    <ProtectedRoute allowedRoles={['Admin']}>
                      <Users />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="change-password"
                  element={
                    <ProtectedRoute>
                      <ChangePassword />
                    </ProtectedRoute>
                  }
                />
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Router>
        </AuthProvider>
      </FeedbackProvider>
    </ThemeProvider>
  );
}

export default App;
